import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import {
  Button,
  ButtonVariantOption,
  ButtonColorOption,
} from "@/components/Button";
import { toast } from "react-toastify";
import { useDeleteMapPlotRequest } from "@/features/map/hooks/useDeleteMapPlotRequest";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { useIsMobile } from "@/hooks/useIsMobile";

interface IProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  selectedIds: number[];
  onSuccess: () => void;
}

export const DeletePlotRequestConfirmModal: React.FC<IProps> = ({
  open,
  setOpen,
  selectedIds,
  onSuccess,
}) => {
  const isMobile = useIsMobile();
  const [isDeleting, setIsDeleting] = useState(false);
  const { trigger: deleteRequest } = useDeleteMapPlotRequest();

  const handleClose = (): void => {
    if (!isDeleting) {
      setOpen(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (selectedIds.length === 0) {
      toast.error("削除対象が選択されていません");
      return;
    }

    setIsDeleting(true);
    try {
      await deleteRequest(selectedIds);

      toast.success(
        `${formatNumberWithComma(
          selectedIds.length
        )}件の保存された物件リストを削除しました`
      );

      onSuccess();
      handleClose();
    } catch (error) {
      toast.error("削除に失敗しました");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        // ダイアログ外クリック、またはエスケープキー押下でダイアログを閉じない
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={isMobile}
    >
      <DialogTitle id="alert-dialog-title">確認画面</DialogTitle>
      <DialogContent>
        <DialogContentText id="alert-dialog-description">
          選択された{formatNumberWithComma(selectedIds.length)}
          件の保存された物件リストを削除します。
        </DialogContentText>
        <DialogContentText>
          この操作は取り消せません。本当に削除しますか？
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button
          label="キャンセル"
          onClick={handleClose}
          variant={ButtonVariantOption.Outlined}
          color={ButtonColorOption.Primary}
          disabled={isDeleting}
        />
        <Button
          label="削除する"
          onClick={() => {
            void handleDelete();
          }}
          variant={ButtonVariantOption.Outlined}
          color={ButtonColorOption.Error}
          disabled={isDeleting}
        />
      </DialogActions>
    </Dialog>
  );
};
